import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight, Share2, Copy, Check, ChevronDown, ChevronUp,
  Heart, Brain, Smartphone, Plane, Plug, BadgeCheck, Wallet, FileText,
  Calendar as CalendarIcon, Mic, MessageSquare, MapPin as MapPinIcon,
  Camera, Utensils, Users as UsersIcon, Sparkles,
} from "lucide-react";
import PageMeta from "../components/PageMeta";
import {
  TalkToKinlyRender, CalendarRender, PollRender,
  ChoresRender, MealsRender, FindMeRender, MomentsRender,
} from "../components/kinly/KinlyRenders";

const API = process.env.REACT_APP_BACKEND_URL;

const TIERS = [
  { id: "free",   name: "Free",   usd: 0,     zar: 0,    blurb: "4 members · 1 GB · Calendar · Kinly basics", best: "Trying it" },
  { id: "lite",   name: "Lite",   usd: 0.99,  zar: 19,   blurb: "Everything in MVP — unlimited", best: "Most families", featured: true },
  { id: "plus",   name: "Plus",   usd: 2.99,  zar: 59,   blurb: "+ Voice · Moments · Circle · Lists", best: "Growing households" },
  { id: "family", name: "Family", usd: 4.99,  zar: 99,   blurb: "+ Meals · Allowance · Health · Budget · Location", best: "Full operators" },
  { id: "pro",    name: "Pro",    usd: 9.99,  zar: 199,  blurb: "+ Coach modes · Documents · Mood AI", best: "Power users" },
  { id: "empire", name: "Empire", usd: 19.99, zar: 399,  blurb: "+ Insurance · Hub display · Priority brain", best: "The whole package" },
];

const FULL_STACK = [
  { Icon: Mic,           name: "AI co-pilot",       blurb: "Voice in. Action out. Kinly understands your people." },
  { Icon: CalendarIcon,  name: "Family calendar",   blurb: "One shared calendar. Colour-coded by member." },
  { Icon: MessageSquare, name: "Polls & decisions", blurb: "Two taps to ask the family. Kinly suggests options." },
  { Icon: BadgeCheck,    name: "Chores & streaks",  blurb: "Assign, tap-done, approve. Stars for kids." },
  { Icon: Utensils,      name: "Family-aware meals",blurb: "Plans around allergies, lunch boxes & busy nights." },
  { Icon: MapPinIcon,    name: "FindMe",            blurb: "Privacy-first family location. No 24/7 tracking." },
  { Icon: Camera,        name: "Moments",           blurb: "Auto-tagged photo timeline tied to events." },
  { Icon: Heart,         name: "Health",            blurb: "Appointments, meds, vaccinations, milestones." },
  { Icon: Brain,         name: "Coach modes",       blurb: "Mood, behaviour and routine patterns — gently surfaced." },
  { Icon: Smartphone,    name: "Screen time",       blurb: "Family-aware rules across iOS and Android." },
  { Icon: Plane,         name: "Vacation & events", blurb: "Itinerary, packing, bookings, birthdays — handled." },
  { Icon: Plug,          name: "Integrations",      blurb: "Google · Apple · Microsoft · WhatsApp · health rings." },
  { Icon: Wallet,        name: "Allowance",         blurb: "Kid wallets, chore-linked stars, family balance." },
  { Icon: UsersIcon,     name: "Circle",            blurb: "Curated feed for grandparents and extended family." },
  { Icon: FileText,      name: "Documents",         blurb: "IDs, insurance, school forms — one secure vault." },
  { Icon: Sparkles,      name: "Hub display",       blurb: "Always-on family screen for kitchen or hallway." },
];

const FAQS = [
  { q: "When does Kinly launch?",
    a: "July 2026. iOS first, Android a week behind. Waitlist members get first access and locked-in founders pricing." },
  { q: "How is this different from Cozi or other family planners?",
    a: "Cozi is a digital fridge. Kinly is a brain. We replace your calendar app, your family WhatsApp group, your photo dump, your chore chart and your meal planner — and it costs less than just the calendar app you're replacing." },
  { q: "What countries will Kinly launch in?",
    a: "Global from day one on iOS and Android. Pricing is localised to your country. The AI handles English, with more languages following through 2026." },
  { q: "Will Kinly talk to apps I already use?",
    a: "Yes. At launch: Google Calendar, Apple Calendar, Microsoft 365, WhatsApp share-in, Apple Health and Google Fit. Slack, Notion, Strava and Spotify follow. The integration API is open for developers." },
  { q: "How does the health side work?",
    a: "Each member has a private health profile — appointments, medications, vaccinations, allergies, growth milestones. Kinly remembers what triggers what, surfaces the next jab due, and quietly nudges before things lapse. Nothing is sold. Nothing leaves your family." },
  { q: "What is a Kinly coach mode?",
    a: "Coach modes are opt-in patterns Kinly watches for — mood, sleep, routine, screen-time vs sport balance, conflict triggers. It surfaces gentle observations to parents, never to kids. Built with child psychologists, not vibes." },
  { q: "Can Kinly handle screen time?",
    a: "Yes — family-aware rules across iOS and Android. Different limits for homework time, weekends, holidays. Kids can earn extra minutes via chore streaks. Parents see the whole picture in one place." },
  { q: "Can Kinly plan a holiday for us?",
    a: "Vacation mode handles the itinerary, the packing list per person, the calendar holds (school, work, sport), shared bookings, and the photo album that comes back. Same engine handles birthdays, school events and recurring family rituals." },
  { q: "Is my family's data safe?",
    a: "Yes. We never sell data. Photos and chats are encrypted at rest. Health data is end-to-end encrypted. You can export everything or delete it all in two taps. Independent annual audit." },
  { q: "Do my kids need their own accounts?",
    a: "You can add kids as members without giving them an account — ideal for under-13s. Older kids get their own login with parental visibility controls. Teens get progressive privacy as they grow." },
  { q: "What about grandparents and extended family?",
    a: "The Circle ring is built for that. They see a curated feed — selected events and photos only. They don't see the family chat or the chore chart. It's the dignified version of a family group." },
  { q: "Does Kinly work for divorced or blended families?",
    a: "Yes. Multi-household support arrives in late 2026 — separate households linked for shared kids, with each parent seeing the full picture without seeing each other's private side. For now, the Circle ring covers the other side." },
  { q: "Powered by Geminii Space — what's that?",
    a: "Geminii Space is the studio behind Kinly, Rogue and Cerebrum AI. Same brain, different products. Kinly is the family one." },
];

function FAQItem({ q, a, isOpen, onClick, idx }) {
  return (
    <div className="border-b border-kinly-line last:border-b-0" data-testid={`kinly-faq-${idx}`}>
      <button
        onClick={onClick}
        className="w-full py-5 flex items-center justify-between text-left gap-4 group"
      >
        <span className="text-[17px] font-medium text-kinly-ink group-hover:text-kinly-coral transition-colors">{q}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-kinly-coral flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-kinly-ink2 flex-shrink-0" />}
      </button>
      {isOpen && (
        <div className="pb-5 pr-8 text-kinly-ink2 text-[15px] leading-[1.65]">{a}</div>
      )}
    </div>
  );
}

function PhoneMockup() {
  return (
    <div className="relative mx-auto" style={{ maxWidth: 340 }}>
      <div className="absolute -inset-10 bg-gradient-to-br from-kinly-peach via-transparent to-transparent blur-3xl opacity-90" />
      <div className="relative rounded-[2.6rem] bg-kinly-ink p-2.5 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.18)]">
        <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-28 h-5 bg-kinly-ink rounded-b-2xl z-10" />
        <div className="rounded-[2.2rem] bg-kinly-bg overflow-hidden aspect-[9/19.5] relative">
          {/* Soft peach atmospheric glow inside the phone — matches real app */}
          <div className="absolute top-0 right-0 w-3/4 h-2/5 pointer-events-none" style={{
            background: "radial-gradient(ellipse at 80% 20%, #FBE9DF 0%, transparent 70%)",
            opacity: 0.95,
          }} />
          {/* Status bar */}
          <div className="relative flex items-center justify-between px-6 pt-4 pb-1">
            <span className="text-[10px] font-medium text-kinly-ink">7:02</span>
            <span className="text-[10px] font-medium text-kinly-ink2">●●●●</span>
          </div>
          {/* Header */}
          <div className="relative px-6 pt-3 pb-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-kinly-coral flex items-center justify-center">
                <span className="font-kinly text-white font-medium text-[13px] leading-none -mt-px">k</span>
              </div>
              <p className="text-[10px] font-medium text-kinly-ink2 uppercase tracking-[0.18em]">Tuesday</p>
            </div>
            <h3 className="font-kinly font-medium text-[24px] text-kinly-ink leading-tight tracking-[-0.02em]">Good morning, Sarah</h3>
            <p className="text-[13px] text-kinly-ink2 mt-1.5">3 things today</p>
          </div>

          <div className="relative px-5 space-y-2">
            {[
              { time: "16:00 · Lily", title: "Piano · Mrs Smith", dot: "bg-kinly-coral" },
              { time: "All day · Household", title: "Bin day — black bin tonight", dot: "bg-kinly-ink" },
              { time: "21:00 · Liam · Action", title: "Swim meet entries close", dot: "bg-kinly-coral", note: "Did you submit? →" },
            ].map((c, i) => (
              <div key={i} className="rounded-2xl bg-white border border-kinly-line p-3.5">
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                  <p className="text-[10px] font-medium text-kinly-ink2 uppercase tracking-[0.14em]">{c.time}</p>
                </div>
                <p className="text-[14px] text-kinly-ink font-medium leading-tight">{c.title}</p>
                {c.note && <p className="text-[11px] text-kinly-coral mt-1 font-medium">{c.note}</p>}
              </div>
            ))}
          </div>

          {/* Kinly input bar */}
          <div className="absolute bottom-4 left-4 right-4 rounded-full bg-kinly-ink p-2.5 pl-3 flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-kinly-coral flex items-center justify-center flex-shrink-0">
              <span className="font-kinly text-white font-medium text-[14px] leading-none -mt-px">k</span>
            </div>
            <p className="text-[12px] text-kinly-bg/70 font-medium">Ask Kinly anything…</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function KinlyPage() {
  const [params] = useSearchParams();
  const referralFromUrl = params.get("ref") || "";
  const [currency, setCurrency] = useState("USD");
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({
    first_name: "", email: "", country: "ZA", family_size_band: "3-4", phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({ total: 0, founders_remaining: 500 });

  useEffect(() => {
    fetch(`${API}/api/geo`).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.country === "ZA") setCurrency("ZAR");
      if (d?.country) setForm(f => ({ ...f, country: d.country }));
    }).catch(() => {});
    fetch(`${API}/api/waitlist/stats?product=kinly`).then(r => r.ok ? r.json() : null).then(d => {
      if (d) setStats(d);
    }).catch(() => {});
  }, []);

  const utm = useMemo(() => ({
    source: params.get("utm_source") || "",
    medium: params.get("utm_medium") || "",
    campaign: params.get("utm_campaign") || "",
  }), [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product: "kinly",
          ...form,
          referred_by_code: referralFromUrl,
          utm_source: utm.source, utm_medium: utm.medium, utm_campaign: utm.campaign,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Something went wrong");
      setSuccess(data);
      setStats(s => ({ ...s, total: s.total + 1, founders_remaining: Math.max(0, s.founders_remaining - 1) }));
    } catch (err) {
      setError(err.message || "Couldn't save your spot — try again?");
    } finally {
      setSubmitting(false);
    }
  };

  const copyShare = () => {
    if (!success?.share_url) return;
    navigator.clipboard.writeText(success.share_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  // SEO: JSON-LD structured data
  const SITE = "https://geminii-ai.com";
  const kinlyJsonLd = useMemo(() => [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "@id": `${SITE}/space/kinly#app`,
      name: "Kinly",
      alternateName: "Kinly — Family Operating System",
      applicationCategory: "LifestyleApplication",
      applicationSubCategory: "Family Organizer",
      operatingSystem: "iOS 16+, Android 11+",
      description: "The family operating system. One calm app for calendar, photos, chores, chat, meals, health, screen time, vacations, and an AI that knows your people. Launching July 2026.",
      url: `${SITE}/space/kinly`,
      image: `${SITE}/og/kinly.png`,
      datePublished: "2026-07-01",
      releaseNotes: "Launching July 2026. Founders pricing: $0.49/mo locked in forever for the first 500 waitlist members.",
      author: { "@id": `${SITE}#organization` },
      publisher: { "@id": `${SITE}#organization` },
      offers: TIERS.map(t => ({
        "@type": "Offer",
        name: `Kinly ${t.name}`,
        price: String(t.usd),
        priceCurrency: "USD",
        category: "subscription",
        availability: "https://schema.org/PreOrder",
        priceValidUntil: "2027-12-31",
        url: `${SITE}/space/kinly`,
      })),
      featureList: FULL_STACK.map(f => f.name).join(", "),
      audience: {
        "@type": "Audience",
        audienceType: "Families with children",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${SITE}/space/kinly#faq`,
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${SITE}/space/kinly#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Geminii",       item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Geminii Space", item: `${SITE}/space` },
        { "@type": "ListItem", position: 3, name: "Kinly",         item: `${SITE}/space/kinly` },
      ],
    },
  ], []);

  return (
    <div className="bg-kinly-bg text-kinly-ink font-kinlyBody antialiased relative overflow-hidden" data-testid="kinly-page">
      <PageMeta
        title="Kinly — Quiet command for your family"
        description="The family operating system. One calm app for calendar, photos, chores, chat, meals, health, screen time, vacations, and an AI that knows your people. Launching July 2026. Founders pricing: $0.49/mo forever — first 500 only."
        path="/space/kinly"
        image="/og/kinly.png"
        jsonLd={kinlyJsonLd}
      />

      {/* HERO */}
      <section className="relative">
        {/* Warm peach atmospheric glow — top-right, matches the real app */}
        <div className="pointer-events-none absolute top-0 right-0 w-[80%] h-[850px] -z-0" aria-hidden="true">
          <div className="absolute inset-0" style={{
            background: "radial-gradient(ellipse 60% 50% at 75% 30%, #FBE9DF 0%, #F6DCCB 25%, transparent 60%)",
            filter: "blur(40px)",
            opacity: 0.85,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2.5 mb-10">
                {/* The real Kinly coral 'k' lockup */}
                <div className="w-7 h-7 rounded-full bg-kinly-coral flex items-center justify-center">
                  <span className="text-white font-kinly font-medium text-[15px] leading-none -mt-px">k</span>
                </div>
                <span className="font-kinly font-medium text-[17px] text-kinly-ink">Kinly</span>
                <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-kinly-ink2 ml-2">By Geminii Space</span>
              </div>
              <h1 className="font-kinly font-medium text-[44px] md:text-[60px] lg:text-[76px] leading-[1.02] tracking-[-0.028em] text-kinly-ink mb-7">
                Your family's<br />quiet co-pilot.
              </h1>
              <p className="text-[17px] md:text-lg text-kinly-ink2 leading-[1.55] mb-10 max-w-xl font-normal">
                One calm app for the calendar, photos, chores, meals, and the AI that actually knows your people. Built so running a household feels like less of a job.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <a
                  href="#waitlist"
                  data-testid="kinly-hero-cta"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-kinly-ink text-kinly-bg font-kinly font-medium text-[15px] hover:bg-black transition-colors"
                >
                  Join the waitlist <ArrowRight size={16} />
                </a>
                <a
                  href="#ai"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-transparent text-kinly-ink font-kinly font-medium text-[15px] hover:text-kinly-coral transition-colors"
                >
                  See how Kinly thinks
                </a>
              </div>
              <p className="text-[12px] uppercase tracking-[0.15em] text-kinly-ink2 font-medium">
                Launching July 2026 · Founders <span className="text-kinly-coral">$0.49 / R9 forever</span> · first {stats.founders_remaining > 0 ? stats.founders_remaining : "500"} only
              </p>
            </div>
            <div className="flex justify-center lg:justify-end">
              <PhoneMockup />
            </div>
          </div>
        </div>
      </section>

      {/* PITCH */}
      <section className="py-20 md:py-28 border-t border-kinly-line">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid md:grid-cols-[1fr_1fr] gap-10 md:gap-16 items-end mb-14">
            <h2 className="font-kinly font-medium text-[36px] md:text-[48px] tracking-[-0.025em] text-kinly-ink leading-[1.05]">
              One app.<br />
              <span className="text-kinly-coral">Not seven.</span>
            </h2>
            <p className="text-[17px] text-kinly-ink2 leading-[1.6] max-w-lg">
              Most families juggle a Cozi calendar, a WhatsApp group, a photo dump, a chore chart, and a meal plan nobody updates. Kinly is just one thing.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { app: "Cozi", note: "Calendar" },
              { app: "WhatsApp family", note: "Chat" },
              { app: "Photo dumps", note: "Memories" },
              { app: "Fridge chart", note: "Chores" },
            ].map((x) => (
              <div key={x.app} className="rounded-xl bg-white border border-kinly-line p-5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-kinly-ink2 mb-2">{x.note}</p>
                <p className="text-[20px] font-semibold text-kinly-ink2 line-through decoration-kinly-coral decoration-2">{x.app}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 pt-12 border-t border-kinly-line grid md:grid-cols-3 gap-8 md:gap-12 max-w-5xl">
            {[
              { title: "One inbox", body: "All the family signal in a single place. No more chasing tabs." },
              { title: "One brain", body: "Kinly remembers preferences, patterns, and what worked." },
              { title: "One bill", body: "Less than the price of just the calendar app you're replacing." },
            ].map(({ title, body }) => (
              <div key={title}>
                <p className="font-kinly font-medium text-[28px] text-kinly-ink mb-2 tracking-tight">{title}</p>
                <p className="text-[15px] text-kinly-ink2 leading-[1.6]">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TALK TO KINLY — flagship AI showcase */}
      <section id="ai" className="py-20 md:py-28 border-t border-kinly-line bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-[1fr_1.05fr] gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-kinly-coral mb-4">Talk to Kinly</p>
              <h2 className="font-kinly font-medium text-[36px] md:text-[48px] tracking-[-0.025em] text-kinly-ink mb-5 leading-[1.05]">
                Say it once.<br />She handles the rest.
              </h2>
              <p className="text-[17px] text-kinly-ink2 leading-[1.6] mb-7 max-w-lg">
                Voice in. Kinly listens, transcribes, understands what you mean — the who, the when, the recurrence, the deadline — and turns it into the right thing in the right place. No taps. No menus.
              </p>
              <div className="space-y-3 max-w-md">
                {[
                  ["1.", "You speak.",         "Hands full. Driving. Walking the dog."],
                  ["2.", "Kinly understands.", "Parses people, dates, times, durations, recurrence."],
                  ["3.", "It's done.",         "Event created. People notified. Calendar updated."],
                ].map(([n,t,b]) => (
                  <div key={t} className="flex items-start gap-3">
                    <span className="text-[11px] font-bold text-kinly-coral w-5 pt-0.5">{n}</span>
                    <div>
                      <p className="text-[15px] font-semibold text-kinly-ink leading-tight">{t}</p>
                      <p className="text-[13px] text-kinly-ink2 mt-0.5">{b}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-[12px] font-medium text-kinly-ink2">
                Same Cerebrum brain that powers Rogue. Different product. Same DNA.
              </p>
            </div>
            <div className="flex justify-center lg:justify-end">
              <TalkToKinlyRender />
            </div>
          </div>
        </div>
      </section>

      {/* CALENDAR */}
      <section className="py-20 md:py-28 border-t border-kinly-line">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1 flex justify-center lg:justify-start">
              <CalendarRender />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-kinly-coral mb-4">Plan</p>
              <h2 className="font-kinly font-medium text-[36px] md:text-[48px] tracking-[-0.025em] text-kinly-ink mb-5 leading-[1.05]">
                Your week.<br />Everyone in it.
              </h2>
              <p className="text-[17px] text-kinly-ink2 leading-[1.6] max-w-lg">
                Colour-coded by family member. Shared by default. Everyone sees what's coming — no more "you didn't tell me." Add an event in four seconds with your voice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* POLLS */}
      <section className="py-20 md:py-28 border-t border-kinly-line bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-[1fr_1.05fr] gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-kinly-coral mb-4">Decide</p>
              <h2 className="font-kinly font-medium text-[36px] md:text-[48px] tracking-[-0.025em] text-kinly-ink mb-5 leading-[1.05]">
                Stop debating.<br />Vote.
              </h2>
              <p className="text-[17px] text-kinly-ink2 leading-[1.6] max-w-lg">
                Two taps to ask the family anything. Kinly suggests options pulled from what you actually liked before — plus one wildcard. Votes close on a timer. Decision made.
              </p>
            </div>
            <div className="flex justify-center lg:justify-end">
              <PollRender />
            </div>
          </div>
        </div>
      </section>

      {/* CHORES */}
      <section className="py-20 md:py-28 border-t border-kinly-line">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1 flex justify-center lg:justify-start">
              <ChoresRender />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-kinly-coral mb-4">Do</p>
              <h2 className="font-kinly font-medium text-[36px] md:text-[48px] tracking-[-0.025em] text-kinly-ink mb-5 leading-[1.05]">
                Chores that<br />don't nag.
              </h2>
              <p className="text-[17px] text-kinly-ink2 leading-[1.6] max-w-lg">
                Assign once. Kids tap done. Parents tap approve. Streaks build. Stars earned. Kinly notices the pattern and praises the kid who's actually showing up.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MEALS */}
      <section className="py-20 md:py-28 border-t border-kinly-line bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-[1fr_1.05fr] gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-kinly-coral mb-4">Feed</p>
              <h2 className="font-kinly font-medium text-[36px] md:text-[48px] tracking-[-0.025em] text-kinly-ink mb-5 leading-[1.05]">
                Meals planned.<br />Groceries sorted.
              </h2>
              <p className="text-[17px] text-kinly-ink2 leading-[1.6] max-w-lg">
                Pick a week of dinners. Kinly generates the shopping list — with quantities — pulled from every recipe. Tick as you go. Skip what's in the cupboard.
              </p>
            </div>
            <div className="flex justify-center lg:justify-end">
              <MealsRender />
            </div>
          </div>
        </div>
      </section>

      {/* FINDME */}
      <section className="py-20 md:py-28 border-t border-kinly-line">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1 flex justify-center lg:justify-start">
              <FindMeRender />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-kinly-coral mb-4">FindMe</p>
              <h2 className="font-kinly font-medium text-[36px] md:text-[48px] tracking-[-0.025em] text-kinly-ink mb-5 leading-[1.05]">
                See everyone.<br />Calmly.
              </h2>
              <p className="text-[17px] text-kinly-ink2 leading-[1.6] max-w-lg">
                Privacy-first family location. No creepy 24/7 tracking — just a quick "all home by 18:30" reassurance when you need it. Each member controls when they're visible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MOMENTS */}
      <section className="py-20 md:py-28 border-t border-kinly-line bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-[1fr_1.05fr] gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-kinly-coral mb-4">Remember</p>
              <h2 className="font-kinly font-medium text-[36px] md:text-[48px] tracking-[-0.025em] text-kinly-ink mb-5 leading-[1.05]">
                The real story of your year.
              </h2>
              <p className="text-[17px] text-kinly-ink2 leading-[1.6] max-w-lg">
                Photos auto-tag to the event they belong to. The swim meet, Sunday lunch, the Magaliesberg hike — all there, in order, a year from now. Not a soup of camera-roll screenshots.
              </p>
            </div>
            <div className="flex justify-center lg:justify-end">
              <MomentsRender />
            </div>
          </div>
        </div>
      </section>

      {/* FULL STACK — every feature at a glance */}
      <section id="stack" className="py-20 md:py-28 border-t border-kinly-line relative overflow-hidden">
        <div className="pointer-events-none absolute top-0 left-0 w-[60%] h-[700px] -z-0" aria-hidden="true">
          <div className="absolute inset-0" style={{
            background: "radial-gradient(ellipse 60% 50% at 20% 30%, #FBE9DF 0%, transparent 60%)",
            filter: "blur(40px)",
            opacity: 0.7,
          }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid md:grid-cols-[1fr_1fr] gap-10 md:gap-16 items-end mb-14">
            <h2 className="font-kinly font-medium text-[36px] md:text-[48px] tracking-[-0.025em] text-kinly-ink leading-[1.05]">
              The full Kinly stack.<br/>
              <span className="text-kinly-coral">Everything, in one calm app.</span>
            </h2>
            <p className="text-[17px] text-kinly-ink2 leading-[1.6] max-w-lg">
              The seven things above are how Kinly thinks. The sixteen below are what it actually does for your family — every day, quietly, without you noticing it's working.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="kinly-full-stack">
            {FULL_STACK.map(({ Icon, name, blurb }) => (
              <div
                key={name}
                data-testid={`kinly-stack-${name.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                className="rounded-2xl bg-white border border-kinly-line p-5 hover:border-kinly-coral/40 transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-kinly-bg border border-kinly-line flex items-center justify-center mb-4">
                  <Icon className="w-4 h-4 text-kinly-coral" strokeWidth={1.8} />
                </div>
                <p className="font-kinly font-medium text-[15px] text-kinly-ink mb-1.5 tracking-[-0.01em]">{name}</p>
                <p className="text-[12px] text-kinly-ink2 leading-[1.5]">{blurb}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-[12px] uppercase tracking-[0.16em] text-kinly-ink2 font-medium text-center">
            All sixteen on every paid tier. Free covers the calendar &amp; Kinly basics.
          </p>
        </div>
      </section>


      {/* FOR EVERY FAMILY */}
      <section className="py-20 md:py-28 border-t border-kinly-line bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
          <h2 className="font-kinly font-medium text-[36px] md:text-[48px] tracking-[-0.025em] text-kinly-ink mb-12 max-w-2xl leading-[1.05]">
            Kinly fits how your family already runs.
          </h2>
          <div className="grid md:grid-cols-3 gap-3">
            {[
              { name: "Sarah",        role: "Mother of two · 38",   initials: "S",  quote: "Mondays were chaos. Now Kinly tells me at 7am what's coming. Piano at four. Bin day. The Friday poll is at 2/4 votes. I open the app once and the whole week makes sense." },
              { name: "Liam",         role: "Twelve",               initials: "L",  quote: "I get pinged when it's my turn for dishes. I tap done. Mom approves. I see my streak. It's not nagging — it's just there." },
              { name: "Anna",         role: "Seventy-one · Circle", initials: "A",  quote: "I get a weekly note: three photos of the kids, what they're up to. I tap, I see, I'm in their life. I'm not asking for an iPad." },
            ].map(({ name, role, initials, quote }) => (
              <div key={name} className="rounded-2xl bg-kinly-bg border border-kinly-line p-7" data-testid={`kinly-persona-${name.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="flex items-center gap-3 mb-5 pb-5 border-b border-kinly-line">
                  <div className="w-10 h-10 rounded-full bg-kinly-ink flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-semibold">{initials}</span>
                  </div>
                  <div>
                    <p className="text-[15px] font-semibold text-kinly-ink leading-tight">{name}</p>
                    <p className="text-[12px] text-kinly-ink2 mt-0.5">{role}</p>
                  </div>
                </div>
                <p className="text-[15px] text-kinly-ink leading-[1.65]">"{quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 md:py-28 border-t border-kinly-line">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid md:grid-cols-[1fr_1fr] gap-10 md:gap-16 items-end mb-10">
            <h2 className="font-kinly font-medium text-[36px] md:text-[48px] tracking-[-0.025em] text-kinly-ink leading-[1.05]">
              Less than a coffee. Per month.
            </h2>
            <p className="text-[17px] text-kinly-ink2 leading-[1.6] max-w-lg">
              Start free. Upgrade when you outgrow it. Cancel anytime. No commitment, no card up front.
            </p>
          </div>

          <div className="rounded-2xl bg-kinly-ink text-kinly-bg p-6 md:p-7 mb-10 flex items-start gap-4" data-testid="kinly-founders-banner">
            <div className="w-10 h-10 rounded-full bg-kinly-coral flex items-center justify-center flex-shrink-0">
              <span className="font-kinly text-white font-medium text-[19px] leading-none -mt-px">k</span>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-kinly-coral2 mb-1.5">Founders pricing</p>
              <p className="text-[16px] leading-[1.55]">
                <span className="font-semibold">$0.49 / R9 per month — forever.</span> First 500 only.
                {stats.founders_remaining > 0 && (
                  <span className="text-kinly-coral2 font-semibold"> {stats.founders_remaining} slots left.</span>
                )}
                {" "}
                <a href="#waitlist" className="underline underline-offset-4 hover:text-kinly-coral2">Claim yours →</a>
              </p>
            </div>
          </div>

          <div className="flex gap-1 p-1 bg-white border border-kinly-line rounded-full w-fit mb-6">
            {["USD","ZAR"].map(c => (
              <button
                key={c}
                onClick={() => setCurrency(c)}
                data-testid={`kinly-currency-${c.toLowerCase()}`}
                className={`px-5 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] rounded-full transition ${currency === c ? "bg-kinly-ink text-white" : "text-kinly-ink2"}`}
              >{c}</button>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {TIERS.map(t => (
              <div
                key={t.id}
                data-testid={`kinly-tier-${t.id}`}
                className={`rounded-xl p-5 border ${t.featured ? "border-kinly-coral bg-kinly-coral/5" : "border-kinly-line bg-white"} relative`}
              >
                {t.featured && <span className="absolute -top-2 left-4 text-[9px] font-semibold uppercase tracking-[0.15em] bg-kinly-coral text-white px-2 py-0.5 rounded-sm">recommended</span>}
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-kinly-ink2 mb-2">{t.name}</p>
                <p className="text-[24px] font-bold text-kinly-ink mb-3 leading-none tracking-tight">
                  {currency === "USD" ? (t.usd === 0 ? "Free" : `$${t.usd}`) : (t.zar === 0 ? "Free" : `R${t.zar}`)}
                  {t.usd !== 0 && <span className="text-xs font-medium text-kinly-ink2 ml-1">/mo</span>}
                </p>
                <p className="text-[12px] text-kinly-ink2 leading-snug mb-3">{t.blurb}</p>
                <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-kinly-ink2">{t.best}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-kinly-ink2">30-day free trial on all paid tiers. No credit card to start.</p>
        </div>
      </section>

      {/* WAITLIST */}
      <section id="waitlist" className="py-20 md:py-28 border-t border-kinly-line bg-kinly-ink text-kinly-bg relative">
        <div className="absolute inset-0 opacity-50 pointer-events-none" style={{
          background: "radial-gradient(circle at 78% 25%, rgba(224,120,86,0.22), transparent 55%)"
        }} />
        <div className="relative max-w-3xl mx-auto px-6 md:px-12">
          {!success ? (
            <>
              <p className="text-center text-[11px] font-medium uppercase tracking-[0.18em] text-kinly-coral mb-4">Join the waitlist</p>
              <h2 className="font-kinly font-medium text-[36px] md:text-[48px] tracking-[-0.025em] mb-5 text-center leading-[1.05]">Founders pricing.<br/>First in line.</h2>
              <p className="text-center text-white/65 leading-[1.6] max-w-xl mx-auto mb-10 text-[17px]">
                Leave your email. We'll let you know the moment Kinly opens. First 500 lock in $0.49/mo for life.
              </p>
              {referralFromUrl && (
                <p className="text-center text-[11px] font-semibold uppercase tracking-[0.15em] text-kinly-coral2 mb-6">
                  Referred by {referralFromUrl} · +10 spots toward founder slot
                </p>
              )}
              <form onSubmit={submit} className="space-y-3" data-testid="kinly-waitlist-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    type="text" required placeholder="First name"
                    value={form.first_name}
                    onChange={e => setForm({ ...form, first_name: e.target.value })}
                    data-testid="kinly-input-name"
                    className="px-5 py-3.5 rounded-xl bg-white/[0.06] border border-white/15 text-white placeholder:text-white/40 outline-none focus:border-kinly-coral2 focus:bg-white/[0.08] transition text-[15px]"
                  />
                  <input
                    type="email" required placeholder="you@yourfamily.com"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    data-testid="kinly-input-email"
                    className="px-5 py-3.5 rounded-xl bg-white/[0.06] border border-white/15 text-white placeholder:text-white/40 outline-none focus:border-kinly-coral2 focus:bg-white/[0.08] transition text-[15px]"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <select
                    value={form.country}
                    onChange={e => setForm({ ...form, country: e.target.value })}
                    data-testid="kinly-input-country"
                    className="px-5 py-3.5 rounded-xl bg-white/[0.06] border border-white/15 text-white outline-none focus:border-kinly-coral2 transition text-[15px]"
                  >
                    <option value="ZA" className="bg-kinly-ink">South Africa</option>
                    <option value="AU" className="bg-kinly-ink">Australia</option>
                    <option value="GB" className="bg-kinly-ink">United Kingdom</option>
                    <option value="US" className="bg-kinly-ink">United States</option>
                    <option value="CA" className="bg-kinly-ink">Canada</option>
                    <option value="NZ" className="bg-kinly-ink">New Zealand</option>
                    <option value="OTHER" className="bg-kinly-ink">Other</option>
                  </select>
                  <select
                    value={form.family_size_band}
                    onChange={e => setForm({ ...form, family_size_band: e.target.value })}
                    data-testid="kinly-input-family"
                    className="px-5 py-3.5 rounded-xl bg-white/[0.06] border border-white/15 text-white outline-none focus:border-kinly-coral2 transition text-[15px]"
                  >
                    <option value="just-me" className="bg-kinly-ink">Just me</option>
                    <option value="2" className="bg-kinly-ink">Two of us</option>
                    <option value="3-4" className="bg-kinly-ink">3–4 in the house</option>
                    <option value="5+" className="bg-kinly-ink">5 or more</option>
                    <option value="extended" className="bg-kinly-ink">Extended / multi-generation</option>
                  </select>
                </div>
                <input
                  type="tel" placeholder="Phone (optional — for launch SMS)"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  data-testid="kinly-input-phone"
                  className="w-full px-5 py-3.5 rounded-xl bg-white/[0.06] border border-white/15 text-white placeholder:text-white/40 outline-none focus:border-kinly-coral2 transition text-[15px]"
                />
                {error && <p className="text-rogue-rose text-sm" data-testid="kinly-form-error">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="kinly-submit"
                  className="w-full py-4 rounded-full bg-kinly-bg text-kinly-ink font-kinly font-medium text-[15px] hover:bg-white transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? "Saving your slot…" : <>Save my founders slot <ArrowRight size={17} /></>}
                </button>
                <p className="text-xs text-center text-white/45 leading-relaxed mt-4">
                  By joining, you agree to receive launch and product emails. Unsubscribe anytime. We never share your data.{" "}
                  <Link to="/legal/privacy" className="underline hover:text-kinly-coral2">Privacy</Link>
                </p>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="kinly-waitlist-success">
              <div className="w-14 h-14 rounded-full bg-kinly-coral/20 border border-kinly-coral2/50 flex items-center justify-center mx-auto mb-6">
                <Check className="w-6 h-6 text-kinly-coral2" strokeWidth={2.5} />
              </div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-kinly-coral2 mb-3">You're on the list</p>
              <h2 className="font-kinly font-medium text-[60px] md:text-[80px] mb-4 leading-none tracking-[-0.04em]">#{success.position}</h2>
              {success.founders_slot_estimated && (
                <p className="text-kinly-coral2 font-semibold uppercase tracking-[0.15em] text-[11px] mb-6">Founders slot · $0.49/mo for life</p>
              )}
              <p className="text-white/65 leading-[1.6] mb-8 max-w-xl mx-auto text-[17px]">
                Share Kinly with one family that might love it. Every share moves you up ten spots.
              </p>
              <div className="flex flex-wrap gap-3 justify-center mb-6">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent("Found this — Kinly. One app for the whole family. " + success.share_url)}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="kinly-share-whatsapp"
                  className="px-5 py-3 rounded-full bg-white/[0.06] hover:bg-white/[0.12] border border-white/15 transition flex items-center gap-2 text-sm"
                >
                  <Share2 size={15} /> WhatsApp
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Joined the Kinly waitlist — one app for our family's calendar, photos, chores + AI. Launching Feb 2026. " + success.share_url)}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="kinly-share-twitter"
                  className="px-5 py-3 rounded-full bg-white/[0.06] hover:bg-white/[0.12] border border-white/15 transition flex items-center gap-2 text-sm"
                >
                  <Share2 size={15} /> Share on X
                </a>
                <button
                  onClick={copyShare}
                  data-testid="kinly-share-copy"
                  className="px-5 py-3 rounded-full bg-kinly-bg text-kinly-ink hover:bg-white transition flex items-center gap-2 text-sm font-kinly font-medium"
                >
                  {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy link</>}
                </button>
              </div>
              <p className="text-[11px] text-white/40 break-all">{success.share_url}</p>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 border-t border-kinly-line">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-kinly font-medium text-[36px] md:text-[48px] tracking-[-0.025em] text-kinly-ink mb-12 leading-[1.05]">Questions, answered.</h2>
          <div>
            {FAQS.map((f, i) => (
              <FAQItem
                key={i}
                idx={i}
                q={f.q}
                a={f.a}
                isOpen={openFaq === i}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Powered by Geminii Space */}
      <div className="py-10 border-t border-kinly-line text-center text-[11px] font-medium uppercase tracking-[0.18em] text-kinly-ink2">
        <Link to="/space" className="hover:text-kinly-coral transition-colors">
          A Geminii Space product
        </Link>
      </div>
    </div>
  );
}
